import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import PaymentMethodSelector from "../components/PaymentMethodSelector";
import mollieService from "../services/mollie";
import { getCurrentUserProfile } from "../services/api";
import { toast } from "react-toastify";

// Styles personnalisés
const styles = {
  checkoutContainer: {
    maxWidth: "1000px",
    margin: "0 auto",
  },
  breadcrumb: {
    backgroundColor: "#f8f9fa",
    padding: "0.75rem 0",
    marginBottom: "1.5rem",
  },
  checkoutStep: {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    padding: "1.5rem",
    marginBottom: "1.5rem",
    boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
  },
  stepNumber: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    backgroundColor: "#543ba4",
    color: "white",
    fontWeight: "bold",
    marginRight: "1rem",
  },
  stepTitle: {
    display: "flex",
    alignItems: "center",
    marginBottom: "1.25rem",
  },
  checkoutSummaryItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0.5rem 0",
    borderBottom: "1px solid #f0f0f0",
  },
  totalRow: {
    fontWeight: "bold",
    fontSize: "1.1rem",
    paddingTop: "0.75rem",
  },
  payButton: {
    backgroundColor: "#543ba4",
    borderColor: "#543ba4",
    color: "white",
    padding: "0.75rem 1.5rem",
    fontWeight: "500",
    width: "100%",
  },
  payButtonProcessing: {
    backgroundColor: "#7e6bca",
    borderColor: "#7e6bca",
    cursor: "not-allowed",
  },
};

const Checkout = () => {
  const { items, totalItems, totalPrice, confirmClearCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(1);
  const [billingInfo, setBillingInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    country: "BE",
  });
  const [shippingInfo, setShippingInfo] = useState({
    sameAsBilling: true,
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    postalCode: "",
    country: "BE",
  });
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [isProfileComplete, setIsProfileComplete] = useState(true);

  // Calculer le montant après remise et les frais de livraison
  const discountAmount = totalPrice * discount;
  const shippingCost = totalPrice >= 50 ? 0 : 4.99;
  const finalTotal = (totalPrice - discountAmount + shippingCost).toFixed(2);

  // Vérifier si le profil est complet
  const checkProfileCompleteness = (user) => {
    return !!(
      user.firstName &&
      user.lastName &&
      user.phone &&
      user.address?.street &&
      user.address?.postalCode &&
      user.address?.city &&
      user.address?.country
    );
  };

  // Récupérer les informations du profil au chargement
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await getCurrentUserProfile();
        const user = response.data;

        // Vérifier si le profil est complet
        const isComplete = checkProfileCompleteness(user);
        setIsProfileComplete(isComplete);

        // Remplir les informations de facturation
        setBillingInfo({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
          address: user.address?.street || "",
          city: user.address?.city || "",
          postalCode: user.address?.postalCode || "",
          country: user.address?.country || "BE",
        });

        // Initialiser les informations de livraison identiques
        setShippingInfo({
          sameAsBilling: true,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          address: user.address?.street || "",
          city: user.address?.city || "",
          postalCode: user.address?.postalCode || "",
          country: user.address?.country || "BE",
        });

        // Récupérer le code promo du localStorage si disponible
        const savedCouponCode = localStorage.getItem("couponCode");
        const savedDiscount = localStorage.getItem("cartDiscount");

        if (savedCouponCode) {
          setCouponCode(savedCouponCode);
        }

        if (savedDiscount) {
          setDiscount(parseFloat(savedDiscount));
        }

        // Si le profil est incomplet, rediriger vers la page de profil
        // SANS afficher de toast - le message sera géré dans Profile.js
        if (!isComplete) {
          navigate("/profile?checkout=pending");
        }
      } catch (error) {
        console.error("Erreur lors du chargement du profil:", error);
        toast.error("Impossible de charger vos informations de profil");
      } finally {
        setLoading(false);
      }
    };

    // Vérifier si le panier est vide
    if (items.length === 0) {
      navigate("/cart");
      toast.warning(
        "Votre panier est vide. Ajoutez des produits avant de procéder au paiement."
      );
      return;
    }

    fetchUserProfile();
  }, [items, navigate]);

  // Gérer le changement dans les champs du formulaire
  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingInfo({
      ...billingInfo,
      [name]: value,
    });

    // Si l'adresse de livraison est identique, mettre à jour aussi
    if (
      shippingInfo.sameAsBilling &&
      (name === "firstName" ||
        name === "lastName" ||
        name === "address" ||
        name === "city" ||
        name === "postalCode" ||
        name === "country")
    ) {
      setShippingInfo({
        ...shippingInfo,
        [name]: value,
      });
    }
  };

  // Gérer le changement dans les champs du formulaire de livraison
  const handleShippingChange = (e) => {
    const { name, value } = e.target;

    if (name === "sameAsBilling") {
      const sameAsBilling = e.target.checked;

      if (sameAsBilling) {
        // Copier les informations de facturation
        setShippingInfo({
          sameAsBilling,
          firstName: billingInfo.firstName,
          lastName: billingInfo.lastName,
          address: billingInfo.address,
          city: billingInfo.city,
          postalCode: billingInfo.postalCode,
          country: billingInfo.country,
        });
      } else {
        // Garder uniquement la valeur sameAsBilling
        setShippingInfo({
          ...shippingInfo,
          sameAsBilling,
        });
      }
    } else {
      // Mettre à jour le champ normalement
      setShippingInfo({
        ...shippingInfo,
        [name]: value,
      });
    }
  };

  // Vérifier la validité du formulaire de l'étape 1
  const isStep1Valid = () => {
    return (
      billingInfo.firstName &&
      billingInfo.lastName &&
      billingInfo.email &&
      billingInfo.phone &&
      billingInfo.address &&
      billingInfo.city &&
      billingInfo.postalCode &&
      billingInfo.country &&
      (shippingInfo.sameAsBilling ||
        (shippingInfo.firstName &&
          shippingInfo.lastName &&
          shippingInfo.address &&
          shippingInfo.city &&
          shippingInfo.postalCode &&
          shippingInfo.country))
    );
  };

  // Passer à l'étape suivante
  const goToNextStep = () => {
    if (step === 1 && !isStep1Valid()) {
      toast.error("Veuillez remplir tous les champs requis.");
      return;
    }

    setStep(step + 1);
    window.scrollTo(0, 0);
  };

  // Revenir à l'étape précédente
  const goToPreviousStep = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  // Gérer la sélection de la méthode de paiement
  const handlePaymentMethodSelect = (method) => {
    setPaymentMethod(method);
  };

  // Appliquer un code promo
  const applyCoupon = () => {
    if (!couponCode.trim()) {
      toast.warning("Veuillez entrer un code promo");
      return;
    }

    // Vérifier si un code promo est déjà appliqué
    if (discount > 0) {
      // Si le même code est appliqué à nouveau, ne rien faire
      if (localStorage.getItem("couponCode") === couponCode) {
        toast.info("Ce code promo est déjà appliqué");
        return;
      }
    }

    if (couponCode.toLowerCase() === "promo10") {
      setDiscount(0.1);
      toast.success("Code promo appliqué : -10%");
      localStorage.setItem("couponCode", couponCode);
      localStorage.setItem("cartDiscount", "0.1");
    } else if (couponCode.toLowerCase() === "promo20") {
      setDiscount(0.2);
      toast.success("Code promo appliqué : -20%");
      localStorage.setItem("couponCode", couponCode);
      localStorage.setItem("cartDiscount", "0.2");
    } else if (couponCode.toLowerCase() === "bienvenue") {
      setDiscount(0.1);
      toast.success("Code promo BIENVENUE appliqué : -10%");
      localStorage.setItem("couponCode", couponCode);
      localStorage.setItem("cartDiscount", "0.1");
    } else {
      toast.error("Code promo invalide");
    }
  };

  // Gérer le processus de paiement
  const handlePayment = async () => {
    if (!paymentMethod) {
      toast.error("Veuillez sélectionner une méthode de paiement.");
      return;
    }

    try {
      setProcessing(true);

      // Générer un ID de commande
      const orderId = mollieService.generateOrderId();

      // Stocker les informations pour la vérification ultérieure (avant l'appel API)
      localStorage.setItem("currentOrderId", orderId);

      // Stocker un timestamp pour vérifier l'expiration
      localStorage.setItem("paymentStartTime", Date.now().toString());

      // Préparer les données de paiement - FORMAT CORRIGÉ pour Mollie
      const paymentData = {
        amount: {
          value: finalTotal,
          currency: "EUR",
        },
        description: `Commande #${orderId} - BoxPlaisir`,
        // Redirection modifiée pour utiliser payment-result au lieu de payment-success
        redirectUrl: `${window.location.origin}/payment-result?order=${orderId}&origin=redirect`,
        webhookUrl: `${window.location.origin}/api/mollie/webhook`,
        method: paymentMethod.method,
        issuer: paymentMethod.issuer,
        // SUPPRIMÉ: expiresAt: getExpirationTime(15)
        metadata: {
          orderId,
          customerId: "guest", // Ou ID client si connecté
          items: items.map((item) => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            isSubscription: item.isSubscription || false,
          })),
          billingInfo,
          shippingInfo,
        },
      };

      console.log("Création du paiement avec les données:", paymentData);

      // Création du paiement via l'API Mollie
      const result = await mollieService.createPayment(paymentData);

      console.log("Réponse de Mollie:", result);

      // Stocker les ID de paiement pour la vérification ultérieure
      localStorage.setItem("molliePaymentId", result.id);

      // Nettoyage des informations temporaires
      localStorage.removeItem("couponCode");
      localStorage.removeItem("cartDiscount");

      // Déterminer l'URL de redirection en fonction de la structure de la réponse
      let checkoutUrl = null;

      // Vérifier toutes les structures possibles de la réponse
      if (result.checkoutUrl) {
        // Propriété ajoutée par notre backend
        checkoutUrl = result.checkoutUrl;
        console.log("Redirection via checkoutUrl:", checkoutUrl);
      } else if (
        result._links &&
        result._links.checkout &&
        result._links.checkout.href
      ) {
        // Structure standard Mollie API
        checkoutUrl = result._links.checkout.href;
        console.log("Redirection via _links.checkout.href:", checkoutUrl);
      } else if (
        result.links &&
        result.links.checkout &&
        result.links.checkout.href
      ) {
        // Structure alternative possible
        checkoutUrl = result.links.checkout.href;
        console.log("Redirection via links.checkout.href:", checkoutUrl);
      }

      if (checkoutUrl) {
        // Redirection vers la page de paiement Mollie
        window.location.href = checkoutUrl;
      } else {
        console.error("URL de checkout non trouvée dans la réponse:", result);

        // Tentative de création d'URL manuelle (dernier recours)
        if (result.id) {
          const fallbackUrl = `https://checkout.mollie.com/payment/${result.id}`;
          console.log("Utilisation d'une URL de fallback:", fallbackUrl);
          window.location.href = fallbackUrl;
        } else {
          throw new Error("Impossible de déterminer l'URL de paiement");
        }
      }
    } catch (error) {
      console.error("Erreur lors du traitement du paiement:", error);
      toast.error(
        "Une erreur est survenue lors du traitement du paiement. Veuillez réessayer."
      );
      setProcessing(false);
    }
  };

  // Fonction pour générer une date d'expiration (ISO 8601) - Maintenue pour compatibilité mais non utilisée
  const getExpirationTime = (minutesFromNow) => {
    const date = new Date();
    date.setMinutes(date.getMinutes() + minutesFromNow);
    return date.toISOString();
  };

  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p className="mt-2">Préparation de votre commande...</p>
      </div>
    );
  }

  return (
    <div style={styles.checkoutContainer} className="py-5">
      {/* Fil d'ariane */}
      <div style={styles.breadcrumb}>
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item">
                <Link to="/">Accueil</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/cart">Panier</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Paiement
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <h1 className="mb-4 text-center">Finalisation de votre commande</h1>

      <div className="row g-4">
        <div className="col-lg-8">
          {/* Informations de livraison et facturation */}
          <div
            style={{
              ...styles.checkoutStep,
              display: step === 1 ? "block" : "none",
            }}
          >
            <div style={styles.stepTitle}>
              <div style={styles.stepNumber}>1</div>
              <h4 className="mb-0">Informations de livraison et facturation</h4>
            </div>

            <form>
              <h5 className="mb-3">Adresse de facturation</h5>
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label htmlFor="firstName" className="form-label">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="firstName"
                    name="firstName"
                    value={billingInfo.firstName}
                    onChange={handleBillingChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="lastName" className="form-label">
                    Nom *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="lastName"
                    name="lastName"
                    value={billingInfo.lastName}
                    onChange={handleBillingChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="email" className="form-label">
                    Email *
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    value={billingInfo.email}
                    onChange={handleBillingChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="phone" className="form-label">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    id="phone"
                    name="phone"
                    value={billingInfo.phone}
                    onChange={handleBillingChange}
                    required
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="address" className="form-label">
                    Adresse *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="address"
                    name="address"
                    value={billingInfo.address}
                    onChange={handleBillingChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="city" className="form-label">
                    Ville *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="city"
                    name="city"
                    value={billingInfo.city}
                    onChange={handleBillingChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="postalCode" className="form-label">
                    Code postal *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="postalCode"
                    name="postalCode"
                    value={billingInfo.postalCode}
                    onChange={handleBillingChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="country" className="form-label">
                    Pays *
                  </label>
                  <select
                    className="form-select"
                    id="country"
                    name="country"
                    value={billingInfo.country}
                    onChange={handleBillingChange}
                    required
                  >
                    <option value="BE">Belgique</option>
                    <option value="FR">France</option>
                    <option value="LU">Luxembourg</option>
                    <option value="NL">Pays-Bas</option>
                    <option value="DE">Allemagne</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="sameAsBilling"
                    name="sameAsBilling"
                    checked={shippingInfo.sameAsBilling}
                    onChange={handleShippingChange}
                  />
                  <label className="form-check-label" htmlFor="sameAsBilling">
                    L'adresse de livraison est identique à l'adresse de
                    facturation
                  </label>
                </div>
              </div>

              {!shippingInfo.sameAsBilling && (
                <>
                  <h5 className="mb-3">Adresse de livraison</h5>
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label htmlFor="shippingFirstName" className="form-label">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="shippingFirstName"
                        name="firstName"
                        value={shippingInfo.firstName}
                        onChange={handleShippingChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="shippingLastName" className="form-label">
                        Nom *
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="shippingLastName"
                        name="lastName"
                        value={shippingInfo.lastName}
                        onChange={handleShippingChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="shippingAddress" className="form-label">
                        Adresse *
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="shippingAddress"
                        name="address"
                        value={shippingInfo.address}
                        onChange={handleShippingChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="shippingCity" className="form-label">
                        Ville *
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="shippingCity"
                        name="city"
                        value={shippingInfo.city}
                        onChange={handleShippingChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label
                        htmlFor="shippingPostalCode"
                        className="form-label"
                      >
                        Code postal *
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="shippingPostalCode"
                        name="postalCode"
                        value={shippingInfo.postalCode}
                        onChange={handleShippingChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="shippingCountry" className="form-label">
                        Pays *
                      </label>
                      <select
                        className="form-select"
                        id="shippingCountry"
                        name="country"
                        value={shippingInfo.country}
                        onChange={handleShippingChange}
                        required
                      >
                        <option value="BE">Belgique</option>
                        <option value="FR">France</option>
                        <option value="LU">Luxembourg</option>
                        <option value="NL">Pays-Bas</option>
                        <option value="DE">Allemagne</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div className="d-flex justify-content-between mt-4">
                <Link to="/cart" className="btn btn-outline-secondary">
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour au panier
                </Link>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={goToNextStep}
                >
                  Continuer
                  <i className="bi bi-arrow-right ms-2"></i>
                </button>
              </div>
            </form>
          </div>

          {/* Méthode de paiement */}
          <div
            style={{
              ...styles.checkoutStep,
              display: step === 2 ? "block" : "none",
            }}
          >
            <div style={styles.stepTitle}>
              <div style={styles.stepNumber}>2</div>
              <h4 className="mb-0">Paiement</h4>
            </div>

            <PaymentMethodSelector onSelect={handlePaymentMethodSelect} />

            <div className="d-flex justify-content-between mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={goToPreviousStep}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour
              </button>
              <button
                type="button"
                className="btn"
                style={{
                  ...styles.payButton,
                  ...(processing ? styles.payButtonProcessing : {}),
                }}
                onClick={handlePayment}
                disabled={!paymentMethod || processing}
              >
                {processing ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Traitement en cours...
                  </>
                ) : (
                  <>
                    Payer {finalTotal} €<i className="bi bi-lock-fill ms-2"></i>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Récapitulatif de la commande */}
        <div className="col-lg-4">
          <div style={styles.checkoutStep}>
            <h4 className="mb-3">Récapitulatif de la commande</h4>

            <div className="mb-3">
              {items.map((item) => (
                <div
                  key={`${item.id}-${item.isSubscription ? "sub" : "single"}`}
                  style={styles.checkoutSummaryItem}
                >
                  <div>
                    <span className="fw-bold">{item.quantity}x</span>{" "}
                    {item.name}
                    {item.isSubscription && (
                      <span
                        className="badge bg-warning text-dark ms-1"
                        style={{ fontSize: "0.7rem" }}
                      >
                        Abo
                      </span>
                    )}
                  </div>
                  <div className="text-end">
                    {(
                      item.price *
                      (item.isSubscription ? 0.85 : 1) *
                      item.quantity
                    ).toFixed(2)}{" "}
                    €
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-4">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Code promo"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={applyCoupon}
                >
                  Appliquer
                </button>
              </div>
            </div>

            <div>
              <div style={styles.checkoutSummaryItem}>
                <span>Sous-total</span>
                <span>{totalPrice.toFixed(2)} €</span>
              </div>

              {discount > 0 && (
                <div style={styles.checkoutSummaryItem}>
                  <span className="text-success">Remise</span>
                  <span className="text-success">
                    -{discountAmount.toFixed(2)} €
                  </span>
                </div>
              )}

              <div style={styles.checkoutSummaryItem}>
                <span>Livraison</span>
                <span>
                  {totalPrice >= 50 ? (
                    <span className="text-success">Gratuite</span>
                  ) : (
                    <span>{shippingCost.toFixed(2)} €</span>
                  )}
                </span>
              </div>

              <div
                style={{ ...styles.checkoutSummaryItem, ...styles.totalRow }}
              >
                <span>Total</span>
                <span>{finalTotal} €</span>
              </div>
            </div>

            <hr className="my-4" />

            <div className="small text-muted">
              <p className="mb-2">
                <i className="bi bi-shield-lock me-1"></i>
                Paiement sécurisé via Mollie
              </p>
              <p className="mb-0">
                <i className="bi bi-info-circle me-1"></i>
                En finalisant votre commande, vous acceptez nos{" "}
                <Link to="/terms-of-service">
                  conditions générales de vente
                </Link>
                .
              </p>
            </div>
          </div>

          {/* Détails de contact et service client */}
          <div style={styles.checkoutStep}>
            <h5 className="mb-3">Besoin d'aide ?</h5>
            <p className="small mb-3">
              Notre équipe de service client est disponible pour vous aider avec
              votre commande.
            </p>
            <div className="d-flex align-items-center mb-2">
              <i className="bi bi-envelope me-2 text-primary"></i>
              <span>support@boxplaisir.be</span>
            </div>
            <div className="d-flex align-items-center">
              <i className="bi bi-telephone me-2 text-primary"></i>
              <span>+32 123 456 789</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
